/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "antd/lib/form/Form";
import { CREATE_CONTRACT, DISABLE_CONTRACT } from "graphql/contract/mutation";
import { GET_SELLERS_FOR_CONTRACT_CREATE } from "graphql/seller/query";
import { UPLOAD_DOCUMENT_MUTATION } from "graphql/upload-document/mutation";
import { useGetSalesmen } from "hooks/order";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { notify } from "utils/helperFuncs";

const useCreateContract = () => {
  const [form] = useForm();
  const history = useHistory();
  const location = useLocation();

  const [loadingCreate, setLoadingCreate] = useState(false);
  const [isConflictModalVisible, setIsConflictModalVisible] = useState(false);
  const [conflictContract, setConflictContract] = useState(null);
  const [pendingCreateInput, setPendingCreateInput] = useState(null);
  const [loadingResolveConflict, setLoadingResolveConflict] = useState(false);
  const [isSaveAndAddNew, setIsSaveAndAddNew] = useState(false);
  const [shouldResetPreview, setShouldResetPreview] = useState(false);

  const preselectedSellerFromState = location?.state?.preselectedSeller;
  const preselectedSellerIdFromQuery = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("sellerId") || undefined;
  }, [location.search]);

  const preselectedSeller = useMemo(() => {
    if (preselectedSellerFromState?.value) {
      return preselectedSellerFromState;
    }

    if (!preselectedSellerIdFromQuery) {
      return undefined;
    }

    return {
      value: preselectedSellerIdFromQuery,
      label: preselectedSellerIdFromQuery,
    };
  }, [preselectedSellerFromState, preselectedSellerIdFromQuery]);

  const initialValues = useMemo(
    () => ({
      agentId: preselectedSeller?.value,
    }),
    [preselectedSeller]
  );

  useEffect(() => {
    if (preselectedSeller?.value) {
      form.setFieldsValue({ agentId: preselectedSeller.value });
    }
  }, [form, preselectedSeller]);

  const [sellerParams, setSellerParams] = useState({
    filters: { query: undefined },
    pagination: { limit: 10, offset: 0 },
  });

  const {
    data: sellersData,
    loading: loadingSellers,
    fetchMore: fetchMoreSellers,
  } = useQuery(GET_SELLERS_FOR_CONTRACT_CREATE, {
    variables: sellerParams,
    fetchPolicy: "cache-first",
  });

  const sellerPagination = sellersData?.seller?.pagination?.paginationData;
  const sellerList = sellersData?.seller?.pagination?.sellers || [];
  const hasMoreSellerOptions = sellerList.length < (sellerPagination?.total || 0);

  const sellerOptions = useMemo(() => {
    const options =
      sellersData?.seller?.pagination?.sellers?.map((s) => ({
        label: s.fullName,
        value: s.id,
      })) || [];

    if (preselectedSeller?.value && !options.some((item) => item.value === preselectedSeller.value)) {
      return [preselectedSeller, ...options];
    }

    return options;
  }, [sellersData, preselectedSeller]);

  const loadMoreSellerOptions = async () => {
    if (!hasMoreSellerOptions || loadingSellers) return;

    await fetchMoreSellers({
      variables: {
        filters: sellerParams?.filters,
        pagination: { offset: sellerList.length, limit: 10 },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.seller?.pagination?.sellers?.length) return previousResult;

        return {
          seller: {
            pagination: {
              paginationData: fetchMoreResult?.seller?.pagination?.paginationData,
              sellers: [
                ...(previousResult?.seller?.pagination?.sellers || []),
                ...fetchMoreResult?.seller?.pagination?.sellers,
              ],
            },
          },
        };
      },
    });
  };

  const onSearchSeller = (value) => {
    setSellerParams((prev) => ({
      ...prev,
      filters: { ...prev.filters, query: value || undefined },
      pagination: { offset: 0, limit: 10 },
    }));
  };

  const [salesmanParams, setSalesmanParams] = useState({
    filters: { query: undefined },
    pagination: { offset: 0, limit: 10 },
  });

  const {
    data: staffOptions,
    loading: loadingStaffs,
    loadMoreSalesmen,
    hasMoreData: hasMoreStaffOptions,
  } = useGetSalesmen(salesmanParams);

  const onSearchSalesman = (value) => {
    setSalesmanParams((prev) => ({
      ...prev,
      filters: { ...prev.filters, query: value || undefined },
      pagination: { offset: 0, limit: 10 },
    }));
  };

  const [createContract] = useMutation(CREATE_CONTRACT);
  const [disableContract] = useMutation(DISABLE_CONTRACT);
  const [uploadFile] = useMutation(UPLOAD_DOCUMENT_MUTATION);

  const contractTypeOptions = [
    { label: "Hợp đồng nguyên tắc", value: "FRAMEWORK_AGREEMENT" },
    { label: "Hợp đồng mua bán", value: "PURCHASE_AGREEMENT" },
  ];

  const isConflictError = (error) => {
    return error?.existingActiveContract;
  };

  const submitCreateContract = async (input, shouldNavigate = true) => {
    const { data } = await createContract({ variables: { input } });

    const result = data?.contractOps?.create;

    if (result?.success && result?.contract?.id) {
      notify.success({ message: "Tạo hợp đồng thành công" });
      if (shouldNavigate) {
        history.push("/list-contract");
      }
      return true;
    }

    if (!result?.success && result?.existingActiveContract) {
      const error = new Error(result.message || "Tạo hợp đồng thất bại");
      error.existingActiveContract = result.existingActiveContract;
      throw error;
    }

    if (!result?.success) {
      notify.error({ message: result.message || "Tạo hợp đồng thất bại" });
      return false;
    }

    return false;
  };

  const handleFinish = async (values) => {
    setLoadingCreate(true);
    let fileURL = "";

    try {
      if (values.contractFile?.[0]?.originFileObj) {
        const uploadRes = await uploadFile({
          variables: { files: [values.contractFile[0].originFileObj] },
        });
        fileURL = uploadRes?.data?.file?.upload?.[0]?.url || "";
      }

      const input = {
        sellerID: values.agentId,
        code: values.contractCode,
        type: values.contractType,
        startDate: values.signDate ? values.signDate.toISOString() : null,
        endDate: values.expiryDate ? values.expiryDate.toISOString() : null,
        fileURL,
        note: values.note,
        drafterID: values.creatorId,
      };

      const shouldNavigate = !isSaveAndAddNew;
      await submitCreateContract(input, shouldNavigate);

      if (isSaveAndAddNew) {
        handleResetForm();
      }
    } catch (error) {
      if (isConflictError(error)) {
        const currentValues = form.getFieldsValue();
        const pendingInput = {
          sellerID: currentValues.agentId,
          code: currentValues.contractCode,
          type: currentValues.contractType,
          startDate: currentValues.signDate ? currentValues.signDate.toISOString() : null,
          endDate: currentValues.expiryDate ? currentValues.expiryDate.toISOString() : null,
          fileURL,
          note: currentValues.note,
          drafterID: currentValues.creatorId,
          isSaveAndAddNew,
        };

        if (error.existingActiveContract) {
          setPendingCreateInput(pendingInput);
          setConflictContract(error.existingActiveContract);
          setIsConflictModalVisible(true);
          return;
        }
      }

      notify.error({ message: error.message || "Có lỗi xảy ra khi tạo hợp đồng" });
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleCloseConflictModal = () => {
    setIsConflictModalVisible(false);
  };

  const handleDisableAndContinueCreate = async () => {
    if (!conflictContract?.id || !pendingCreateInput) return;

    setLoadingResolveConflict(true);
    try {
      await disableContract({ variables: { id: conflictContract.id } });
      const { isSaveAndAddNew: pendingIsSaveAndAddNew, ...input } = pendingCreateInput;
      const shouldNavigate = !pendingIsSaveAndAddNew;
      await submitCreateContract(input, shouldNavigate);
      setIsConflictModalVisible(false);

      if (pendingIsSaveAndAddNew) {
        handleResetForm();
      }
    } catch (error) {
      notify.error({ message: error?.message || "Không thể vô hiệu hóa hợp đồng hiện tại" });
    } finally {
      setLoadingResolveConflict(false);
    }
  };

  const handleResetForm = () => {
    form.resetFields();
    setIsSaveAndAddNew(false);
    setShouldResetPreview(true);
  };

  return {
    form,
    history,
    handleFinish,
    initialValues,
    loadingCreate: loadingCreate || loadingSellers || loadingStaffs,
    sellerOptions,
    staffOptions,
    contractTypeOptions,
    hasMoreSellerOptions,
    loadMoreSellerOptions,
    onSearchSeller,
    hasMoreStaffOptions,
    loadMoreSalesmen,
    onSearchSalesman,
    isConflictModalVisible,
    conflictContract,
    loadingResolveConflict,
    handleCloseConflictModal,
    handleDisableAndContinueCreate,
    isSaveAndAddNew,
    setIsSaveAndAddNew,
    handleResetForm,
    shouldResetPreview,
    setShouldResetPreview,
  };
};

export default useCreateContract;
